import moment from 'moment';
import { isDistributionTask, isOrderBasedTask } from './taskUtils';

export const ACTIVITY_MODE = {
  STANDARD: 'STANDARD',
  VISIT_ONLY: 'VISIT_ONLY',
};

const parseRouteDate = (value) => {
  if (!value) return null;
  const strictDate = moment(value, 'DD-MM-YYYY', true);
  if (strictDate.isValid()) return strictDate.startOf('day');
  const parsed = moment(value);
  return parsed.isValid() ? parsed.startOf('day') : null;
};

const isScheduleActiveOn = (schedule, currentDate) => {
  if (!schedule) return false;

  const day = Number(schedule.dayOfVisit);
  const frequency = Number(schedule.frequency);
  const hasLastVisitDate = Boolean(schedule.lastTimeOrder);
  const lastVisitDate = parseRouteDate(schedule.lastTimeOrder);
  const dayMatches = currentDate.isoWeekday() === day;

  if (!dayMatches) return false;

  if (!hasLastVisitDate) return true;
  if (!lastVisitDate) return false;
  if (lastVisitDate.isAfter(currentDate)) return false;
  if (!Number.isFinite(frequency) || frequency <= 0) return true;

  return currentDate.diff(lastVisitDate, 'weeks') % frequency === 0;
};

export const isRouteScheduleActive = (schedule, onDate) => {
  if (!onDate) return false;
  const currentDate = moment(onDate).startOf('day');
  if (!currentDate.isValid()) return false;
  return isScheduleActiveOn(schedule, currentDate);
};

export const getActiveRouteSchedules = (routeSchedules, onDate) => {
  if (!Array.isArray(routeSchedules) || routeSchedules.length === 0) return [];
  if (!onDate) return [];
  const currentDate = moment(onDate).startOf('day');
  if (!currentDate.isValid()) return [];
  return routeSchedules.filter((schedule) =>
    isScheduleActiveOn(schedule, currentDate),
  );
};

export const isFlagSet = (value) =>
  String(value || '')
    .trim()
    .toUpperCase() === 'D';

const isVisitSchedule = (schedule) =>
  isFlagSet(schedule?.isVisit) && !isFlagSet(schedule?.isSale);

export const getEffectiveRouteSchedule = (routeSchedules, onDate) => {
  const activeSchedules = getActiveRouteSchedules(routeSchedules, onDate);
  if (activeSchedules.length === 0) return null;

  return (
    activeSchedules.find((schedule) => !isVisitSchedule(schedule)) ||
    activeSchedules[0]
  );
};

export const resolveActivityMode = (routeSchedules, onDate) => {
  const activeSchedules = getActiveRouteSchedules(routeSchedules, onDate);

  if (activeSchedules.length === 0) return ACTIVITY_MODE.STANDARD;

  const hasStandardSchedule = activeSchedules.some(
    (schedule) => !isVisitSchedule(schedule),
  );

  return hasStandardSchedule
    ? ACTIVITY_MODE.STANDARD
    : ACTIVITY_MODE.VISIT_ONLY;
};

export const isArticleLinkedTask = (task) =>
  isOrderBasedTask(task) ||
  isDistributionTask(task) ||
  task?.raster === 'D' ||
  task?.ulistan === 'D' ||
  (Array.isArray(task?.products) && task.products.length > 0);

export const isTaskRequired = (task, activityMode) => {
  if (task?.priority !== 'D') return false;
  if (activityMode !== ACTIVITY_MODE.VISIT_ONLY) return true;
  return !isArticleLinkedTask(task);
};

export const isTaskOptionalInVisit = (task, activityMode) =>
  activityMode === ACTIVITY_MODE.VISIT_ONLY &&
  task?.priority === 'D' &&
  isArticleLinkedTask(task);

export const isSurveyRequired = (
  survey,
  activityMode,
  isRemoteVisit,
) => {
  if (isRemoteVisit) return false;
  if (activityMode === ACTIVITY_MODE.VISIT_ONLY) return false;
  return survey?.obavezna === 'D';
};

export const shouldAutoRecordVisit = ({
  activityMode,
  isRemoteVisit,
  hasOrder,
  hasVisit,
}) =>
  isRemoteVisit === false &&
  !hasVisit &&
  (hasOrder || activityMode === ACTIVITY_MODE.VISIT_ONLY);

const mergeRouteSchedules = (current, incoming) => {
  const safeCurrent = Array.isArray(current) ? current : [];
  const safeIncoming = Array.isArray(incoming) ? incoming : [];
  const schedules = new Map();
  [...safeCurrent, ...safeIncoming].forEach((schedule) => {
    if (!schedule) return;
    const key = schedule.id
      ? String(schedule.id)
      : `${schedule.salesman || ''}_${schedule.dayOfVisit || ''}_${
          schedule.frequency || ''
        }_${schedule.isVisit || ''}_${schedule.lastTimeOrder || ''}`;
    schedules.set(key, schedule);
  });
  return Array.from(schedules.values());
};

const splitLegacyColumn = (value) =>
  value === null || value === undefined ? [] : String(value).split(',');

const getLegacyRouteSchedules = (customer) => {
  const days = splitLegacyColumn(customer?.visitDay);
  const frequencies = splitLegacyColumn(customer?.visitFrequency);
  const lastVisits = splitLegacyColumn(customer?.lastOrderDate);

  return days
    .map((day, index) => ({
      dayOfVisit: day?.trim(),
      frequency: frequencies[index],
      lastTimeOrder: lastVisits[index],
      isVisit: customer?.isVisit,
    }))
    .filter((schedule) => schedule.dayOfVisit);
};

const mergePreferringValues = (existing, incoming) => {
  const merged = { ...existing };
  Object.entries(incoming).forEach(([key, value]) => {
    if (value !== null && value !== undefined) merged[key] = value;
  });
  return merged;
};

export const deduplicateCustomers = (customers) => {
  if (!Array.isArray(customers)) return [];

  const customersById = new Map();
  customers.forEach((customer) => {
    if (!customer?.id) return;
    const key = String(customer.id);
    const existing = customersById.get(key);

    if (!existing) {
      customersById.set(key, {
        ...customer,
        routeSchedules: mergeRouteSchedules([], customer.routeSchedules),
        legacyRouteSchedules: getLegacyRouteSchedules(customer),
      });
      return;
    }

    customersById.set(key, {
      ...mergePreferringValues(existing, customer),
      dailyOrderCount: Math.max(
        Number(existing.dailyOrderCount) || 0,
        Number(customer.dailyOrderCount) || 0,
      ),
      dailyVisitsCount: Math.max(
        Number(existing.dailyVisitsCount) || 0,
        Number(customer.dailyVisitsCount) || 0,
      ),
      routeSchedules: mergeRouteSchedules(
        existing.routeSchedules,
        customer.routeSchedules,
      ),
      legacyRouteSchedules: mergeRouteSchedules(
        existing.legacyRouteSchedules,
        getLegacyRouteSchedules(customer),
      ),
    });
  });

  return Array.from(customersById.values());
};

export const withActivityMode = (customer, onDate = moment()) => {
  const hasStructuredSchedules =
    Array.isArray(customer?.routeSchedules) &&
    customer.routeSchedules.length > 0;
  const activityMode = hasStructuredSchedules
    ? resolveActivityMode(customer.routeSchedules, onDate)
    : ACTIVITY_MODE.STANDARD;

  return {
    ...customer,
    activityMode,
    activityDate: moment(onDate).format('YYYY-MM-DD'),
  };
};

export const ensureFreshActivityMode = (customer, onDate = moment()) => {
  if (!customer) return customer;
  const today = moment(onDate).format('YYYY-MM-DD');
  if (customer.activityDate === today && customer.activityMode) {
    return customer;
  }
  return withActivityMode(customer, onDate);
};
